import {
  CREATE_GRID_ROW,
  CellId,
  FRACTIONAL_INDEX_END,
  FRACTIONAL_INDEX_START,
  GridColumn,
  GridColumnId,
  GridElement,
  GridElementEntityId,
  GridElementId,
  GridElementType,
  GridLayoutId,
  GridRowId,
  HexVersionAtomicOperation,
  NUM_ROW_SEGMENTS,
  SharedFilterId,
  assertNever,
  findGridElements,
  fractionalIndexMidpoint95,
  removeElementsFully,
  sortSubsetInSameOrder,
  typedObjectKeys,
  uuid,
} from "@hex/common";
import { groupBy } from "lodash";
import { useCallback } from "react";

import { DispatchAOResult } from "../../../../atomic-operations/index.js";
import { useCellContentsGetter } from "../../../../hex-version-multiplayer/state-hooks/cellContentsStateHooks.js";
import { useGridRowsGetter } from "../../../../hex-version-multiplayer/state-hooks/gridRowsStateHooks";
import { useHexVersionGetter } from "../../../../hex-version-multiplayer/state-hooks/hexVersionStateHooks";
import { useStore } from "../../../../redux/hooks";
import {
  GridLayoutMP,
  hexVersionMPSelectors,
} from "../../../../redux/slices/hexVersionMPSlice";
import { isInlineCell } from "../../../../util/cellLayoutHelpers";
import { useHexVersionAOContext } from "../../../../util/hexVersionAOContext";
import { useSelectedTabIdGetter } from "../GridTabHooks.js";
import { gridRowMPToGridRow } from "../utils/gridUtils.js";

import { useDispatchGridRowChanges } from "./useDispatchGridLayoutActions";

export const getDefaultStartingWidthForCell = (
  isInlineCellType: boolean,
): number =>
  isInlineCellType ? Math.floor(NUM_ROW_SEGMENTS / 4) : NUM_ROW_SEGMENTS;

const newGridColumnForEntity = ({
  entityId,
  type,
  width,
}: {
  type: GridElementType;
  entityId: GridElementEntityId;
  width: number;
}): readonly GridColumn[] => {
  return [
    {
      id: uuid() as GridColumnId,
      start: 0,
      end: width,
      gridElements: [newGridElementForEntity(type, entityId)],
    },
  ];
};

function newGridElementForEntity(
  type: GridElementType,
  entityId: GridElementEntityId,
): GridElement {
  const id = uuid() as GridElementId;
  if (type === GridElementType.CELL) {
    return {
      id,
      type,
      entityId: entityId as CellId,
    };
  } else if (type === GridElementType.SHARED_FILTER) {
    return {
      id,
      type,
      entityId: entityId as SharedFilterId,
    };
  } else {
    assertNever(type, type);
  }
}

interface AddCellToGridLayoutArgs {
  type: typeof GridElementType.CELL;
  isInline?: boolean;
  entityId: CellId;
  tabId?: GridLayoutId;
  insertAt?: string;
}

interface AddSharedFilterToGridLayoutArgs {
  type: typeof GridElementType.SHARED_FILTER;
  entityId: SharedFilterId;
  tabId?: GridLayoutId;
  insertAt?: string;
}

type AddEntityToGridLayoutArgs =
  | AddCellToGridLayoutArgs
  | AddSharedFilterToGridLayoutArgs;

interface ReturnVal {
  addEntityToGridLayoutOperations: (args: AddEntityToGridLayoutArgs) => {
    gridElementId?: GridElementId;
    operations: HexVersionAtomicOperation[];
  };
  addEntityToGridLayout: (args: AddEntityToGridLayoutArgs) => DispatchAOResult;
  removeEntityFromGridLayoutOperations: (
    entityId: GridElementEntityId,
    tabId?: GridLayoutId,
  ) => HexVersionAtomicOperation[];
  removeEntityFromCurrentGridLayout: (
    entityId: GridElementEntityId,
  ) => DispatchAOResult;
}

/** Returns callbacks which can be used to add/remove GridElements for specific cells */
export const useAddEntityToGridLayout = (): ReturnVal => {
  const { dispatchAO } = useHexVersionAOContext();
  const { dispatchGridRowChangesOperations } = useDispatchGridRowChanges();
  const store = useStore();

  const getCellContents = useCellContentsGetter();
  const getGridRows = useGridRowsGetter();
  const getHexVersion = useHexVersionGetter();
  const getSelectedTabId = useSelectedTabIdGetter();

  const addEntityToGridLayoutOperations = useCallback(
    ({
      entityId,
      insertAt: insertAtOverride,
      tabId,
      type,
      ...rest
    }: AddEntityToGridLayoutArgs) => {
      const hexVersionId = getHexVersion().id;
      const selectedTabId = getSelectedTabId();

      let isInline: boolean;
      if ("isInline" in rest && rest.isInline != null) {
        isInline = rest.isInline;
      } else if (type === GridElementType.CELL) {
        const cellContents = getCellContents(entityId);
        isInline = cellContents != null && isInlineCell(cellContents);
      } else if (type === GridElementType.SHARED_FILTER) {
        isInline = true;
      } else {
        assertNever(type, type);
      }

      let gridLayout: GridLayoutMP | undefined | null;
      if (tabId != null) {
        gridLayout = hexVersionMPSelectors
          .getGridLayoutSelectors(hexVersionId)
          .selectById(store.getState(), tabId);
      } else if (selectedTabId != null) {
        gridLayout = hexVersionMPSelectors
          .getGridLayoutSelectors(hexVersionId)
          .selectById(store.getState(), selectedTabId);
      } else {
        return {
          operations: [],
        };
      }

      if (gridLayout == null) {
        return {
          operations: [],
        };
      }

      const gridLayoutId = gridLayout.id;
      const gridRows = getGridRows().filter(
        (r) => r.gridLayoutId === gridLayoutId,
      );

      const newElementWidth = getDefaultStartingWidthForCell(isInline);

      let insertAt: string;
      if (insertAtOverride != null) {
        insertAt = insertAtOverride;
      } else if (type === GridElementType.SHARED_FILTER) {
        // TODO: handle shared filter insert at case
        insertAt = FRACTIONAL_INDEX_START.FRACTIONAL_INDEX_START;
      } else if (type === GridElementType.CELL) {
        // We do our best to preserve a reasonable layout when adding the element:
        //   1) We find the closest previous cell in Notebook view that has a grid element
        //   2) We find where that cell's grid element is in the grid
        //   3) We insert our new element in a new row below that grid element
        // TODO: possibly make sure that inline cells are added to the existing row
        // if the previous cell is also an inline

        // Get info about all cells in Notebook view
        const cells = hexVersionMPSelectors
          .getCellSelectors(hexVersionId)
          .selectFlattenedSorted(store.getState());
        const sortedCellIds = cells.map((cell) => cell.id);

        // Get info about cell ids used in the grid
        const allCellElementsInTab = gridRows
          .flatMap((row) => row.gridColumns)
          .flatMap((col) => col.gridElements)
          .filter((element) => element.type === GridElementType.CELL);
        const elementsByCellId = groupBy(
          allCellElementsInTab,
          (ele) => ele.entityId,
        );
        const unsortedCellIdsUsedInGrid = typedObjectKeys(elementsByCellId);

        const sortedCellIdsToUseInGrid = sortSubsetInSameOrder(
          [...unsortedCellIdsUsedInGrid, entityId],
          sortedCellIds,
        );
        const newCellIdx = sortedCellIdsToUseInGrid.indexOf(entityId);

        if (newCellIdx <= 0) {
          // If our cell is before all other user cells, then we just insert at the top
          insertAt = fractionalIndexMidpoint95(
            FRACTIONAL_INDEX_START,
            gridRows[0]?.order ?? FRACTIONAL_INDEX_END,
          );
        } else {
          // Otherwise, we need to find what row the previous cell's grid element is in,
          // and then insert in a new row after that.

          const prevCellIdx = newCellIdx - 1;
          const prevCellId = sortedCellIdsToUseInGrid[prevCellIdx];
          // (if there are multiple elements for the previous cell, we just pick the first one to look at)
          const prevElement = elementsByCellId[prevCellId][0];
          const prevElementRowIdx = gridRows.findIndex((row) =>
            row.gridColumns.some((col) =>
              col.gridElements.some((ele) => ele.id === prevElement.id),
            ),
          );

          const prevRowOrder =
            prevElementRowIdx >= 0
              ? gridRows[prevElementRowIdx].order
              : FRACTIONAL_INDEX_START;
          const nextRowOrder =
            prevElementRowIdx >= 0 && prevElementRowIdx <= gridRows.length - 2
              ? gridRows[prevElementRowIdx + 1].order
              : FRACTIONAL_INDEX_END;
          insertAt = fractionalIndexMidpoint95(prevRowOrder, nextRowOrder);
        }
      } else {
        assertNever(type, type);
      }

      const gridColumns = newGridColumnForEntity({
        type,
        entityId,
        width: newElementWidth,
      });
      const gridElementId = gridColumns[0].gridElements[0].id;

      return {
        gridElementId,
        operations: [
          CREATE_GRID_ROW.create({
            gridColumns,
            gridLayoutId,
            gridRowId: uuid() as GridRowId,
            insertAt,
          }),
        ],
      };
    },
    [getHexVersion, getSelectedTabId, getGridRows, getCellContents, store],
  );

  const addEntityToGridLayout = useCallback(
    (args: AddEntityToGridLayoutArgs) => {
      return dispatchAO(addEntityToGridLayoutOperations(args).operations);
    },
    [addEntityToGridLayoutOperations, dispatchAO],
  );

  const removeEntityFromGridLayoutOperations = useCallback(
    (entityId: GridElementEntityId, tabId?: GridLayoutId) => {
      const gridRows = getGridRows().map(gridRowMPToGridRow);

      const hexVersionId = getHexVersion().id;

      const gridLayout = hexVersionMPSelectors
        .getGridLayoutSelectors(hexVersionId)
        .selectPrimary(store.getState());

      const gridLayoutId = gridLayout.id;

      // Remove all the elements matching our `cellId`
      const elementsToRemove = findGridElements(
        tabId != null
          ? gridRows.filter((r) => r.gridLayoutId === tabId)
          : gridRows,
        (ele) => ele.entityId === entityId,
      );
      const { modifiedGridRowIds, newGridRows, removedGridRowIds } =
        removeElementsFully(gridRows, elementsToRemove);

      return dispatchGridRowChangesOperations({
        gridLayoutId,
        gridRows: newGridRows,
        modifiedGridRowIds,
        removedGridRowIds,
      });
    },
    [getGridRows, getHexVersion, store, dispatchGridRowChangesOperations],
  );

  const removeEntityFromCurrentGridLayout = useCallback(
    (entityId: GridElementEntityId) => {
      const tabId = getSelectedTabId();
      return dispatchAO(removeEntityFromGridLayoutOperations(entityId, tabId));
    },
    [getSelectedTabId, dispatchAO, removeEntityFromGridLayoutOperations],
  );

  return {
    addEntityToGridLayout,
    removeEntityFromCurrentGridLayout,
    removeEntityFromGridLayoutOperations,
    addEntityToGridLayoutOperations,
  };
};
